import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './PipelinesList.css';

const PipelinesList = () => {
  usePageTitle('Pipelines');
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [layerFilter, setLayerFilter] = useState('all');

  useEffect(() => {
    fetchPipelines();
  }, []);

  const fetchPipelines = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/etl/pipelines/');
      setPipelines(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch pipelines:', err);
      setError('Failed to load pipelines');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerPipeline = async (e, pipelineId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axiosInstance.post(`/etl/pipelines/${pipelineId}/trigger/`);
      fetchPipelines();
    } catch (err) {
      console.error('Failed to trigger pipeline:', err);
      alert('Failed to trigger pipeline');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'inactive': return 'status-badge status-inactive';
      case 'archived': return 'status-badge status-archived';
      default: return 'status-badge';
    }
  };

  const getLayerBadgeClass = (layer) => {
    switch (layer) {
      case 'bronze': return 'layer-badge layer-bronze';
      case 'silver': return 'layer-badge layer-silver';
      case 'gold': return 'layer-badge layer-gold';
      default: return 'layer-badge';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getSuccessRate = (pipeline) => {
    if (pipeline.total_runs === 0) return '-';
    return ((pipeline.successful_runs / pipeline.total_runs) * 100).toFixed(0) + '%';
  };

  const filteredPipelines = pipelines.filter(pipeline => {
    const matchesSearch = pipeline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pipeline.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || pipeline.status === statusFilter;
    const matchesLayer = layerFilter === 'all' ||
                         (pipeline.flow_types_summary && pipeline.flow_types_summary.includes(layerFilter));
    return matchesSearch && matchesStatus && matchesLayer;
  });

  if (loading) {
    return (
      <div className="pipelines-container">
        <div className="loading-state">Loading pipelines...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pipelines-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="pipelines-container">
      <div className="pipelines-header">
        <div className="header-title">
          <h1>Pipelines</h1>
          <p className="header-subtitle">Manage grouped ETL flows and run them together</p>
        </div>
        <button
          className="btn-create-pipeline"
          onClick={() => navigate('/pipelines/new')}
        >
          + Create Pipeline
        </button>
      </div>

      <div className="pipelines-filters">
        <input
          type="text"
          placeholder="Search pipelines..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="filter-chips">
          <button
            className={`filter-chip ${layerFilter === 'all' ? 'active' : ''}`}
            onClick={() => setLayerFilter('all')}
          >
            All Layers
          </button>
          <button
            className={`filter-chip filter-bronze ${layerFilter === 'bronze' ? 'active' : ''}`}
            onClick={() => setLayerFilter('bronze')}
          >
            Bronze
          </button>
          <button
            className={`filter-chip filter-silver ${layerFilter === 'silver' ? 'active' : ''}`}
            onClick={() => setLayerFilter('silver')}
          >
            Silver
          </button>
          <button
            className={`filter-chip filter-gold ${layerFilter === 'gold' ? 'active' : ''}`}
            onClick={() => setLayerFilter('gold')}
          >
            Gold
          </button>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {filteredPipelines.length === 0 ? (
        <div className="empty-state">
          <h3>No pipelines found</h3>
          <p>Create a pipeline to group related ETL flows together</p>
          <button
            className="btn-create-pipeline"
            onClick={() => navigate('/pipelines/new')}
          >
            Create Your First Pipeline
          </button>
        </div>
      ) : (
        <div className="pipelines-table-wrapper">
          <table className="pipelines-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th className="text-center">Flows</th>
                <th>Layers</th>
                <th className="text-center">Runs</th>
                <th className="text-center">Success</th>
                <th className="text-center">Failed</th>
                <th className="text-center">Rate</th>
                <th>Last Run</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPipelines.map((pipeline) => (
                <tr
                  key={pipeline.id}
                  onClick={() => navigate(`/pipelines/${pipeline.id}`)}
                  className="pipeline-row"
                >
                  <td>
                    <div className="pipeline-name-cell">
                      <span className="pipeline-name">{pipeline.name}</span>
                      {pipeline.description && (
                        <span className="pipeline-description">{pipeline.description}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(pipeline.status)}>
                      {pipeline.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className="flow-count">{pipeline.step_count || 0}</span>
                  </td>
                  <td>
                    <div className="layer-badges">
                      {pipeline.flow_types_summary && pipeline.flow_types_summary.includes('bronze') && (
                        <span className={getLayerBadgeClass('bronze')}>B</span>
                      )}
                      {pipeline.flow_types_summary && pipeline.flow_types_summary.includes('silver') && (
                        <span className={getLayerBadgeClass('silver')}>S</span>
                      )}
                      {pipeline.flow_types_summary && pipeline.flow_types_summary.includes('gold') && (
                        <span className={getLayerBadgeClass('gold')}>G</span>
                      )}
                    </div>
                  </td>
                  <td className="text-center">
                    <span className="stat-total">{pipeline.total_runs}</span>
                  </td>
                  <td className="text-center">
                    <span className="stat-success">{pipeline.successful_runs}</span>
                  </td>
                  <td className="text-center">
                    <span className="stat-failed">{pipeline.failed_runs}</span>
                  </td>
                  <td className="text-center">
                    <span className="stat-rate">{getSuccessRate(pipeline)}</span>
                  </td>
                  <td>
                    <span className="last-run">{formatDate(pipeline.last_run_at)}</span>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn-run-pipeline"
                      onClick={(e) => handleTriggerPipeline(e, pipeline.id)}
                      disabled={pipeline.status !== 'active'}
                    >
                      Run
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PipelinesList;
